import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { RadarChart } from "../components/ui/RadarChart";
import { ScoreRing } from "../components/ui/ScoreRing";
import { BarChart } from "../components/ui/BarChart";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import type { PersonaProfile, TeamFitTraits } from "../types";

type Match = {
  id: string;
  company_name: string;
  team_fit_score: number;
  career_fit_score: number;
  status: string;
  created_at: string;
};
type ResumeItem = { id: string; target_company_name: string | null; created_at: string };
type InterviewItem = { id: string; title: string; status: string; question_count: number; created_at: string };

const STRENGTH_COPY: Record<keyof TeamFitTraits, { icon: string; title: string; desc: string }> = {
  communication: { icon: "💬", title: "Effective Communicator", desc: "Communicates clearly across different audiences." },
  collaboration: { icon: "🤝", title: "Team Player", desc: "Works well with diverse teams toward shared goals." },
  leadership: { icon: "🧭", title: "Natural Leader", desc: "Takes ownership and supports others under pressure." },
  problemSolving: { icon: "💡", title: "Creative Thinker", desc: "Finds practical solutions to new problems." },
  adaptability: { icon: "🌊", title: "Adaptable", desc: "Adjusts quickly to changing environments." },
  integrity: { icon: "🛡️", title: "Trustworthy", desc: "Follows through and communicates with honesty." },
};

function avgTraits(t?: TeamFitTraits | null) {
  if (!t) return 0;
  const values = Object.values(t);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function monthLabel(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short" });
}

/** 최근 6개월 동안 생성된 이력서+면접 수를 월별로 집계 (실제 활동 데이터 기반) */
function buildActivitySeries(resumes: ResumeItem[], interviews: InterviewItem[]) {
  const now = new Date();
  const months: { key: string; label: string; value: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: monthLabel(d.toISOString()), value: 0 });
  }
  const bump = (iso: string) => {
    const d = new Date(iso);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const m = months.find((mo) => mo.key === key);
    if (m) m.value += 1;
  };
  resumes.forEach((r) => bump(r.created_at));
  interviews.forEach((iv) => bump(iv.created_at));
  return months;
}

export default function TalentDashboard() {
  const { user } = useAuth();
  const [persona, setPersona] = useState<PersonaProfile | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [resumes, setResumes] = useState<ResumeItem[]>([]);
  const [interviews, setInterviews] = useState<InterviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appFilter, setAppFilter] = useState<"all" | string>("all");
  const [shareCopied, setShareCopied] = useState(false);

  async function handleSharePersona() {
    const url = window.location.origin + "/app/persona";
    if (navigator.share) {
      try {
        await navigator.share({ title: `${user?.name || "My"} Persona`, url });
        return;
      } catch {
        // Fallback to clipboard
      }
    }
    let success = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        success = true;
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch {
      success = false;
    }
    if (success) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const [p, m, r, iv] = await Promise.all([
          api.get<PersonaProfile>("/api/persona"),
          api.get<{ items: Match[] }>("/api/teamfit/matches"),
          api.get<{ items: ResumeItem[] }>("/api/resume"),
          api.get<{ items: InterviewItem[] }>("/api/interview"),
        ]);
        setPersona(p);
        setMatches(m.items);
        setResumes(r.items);
        setInterviews(iv.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <AppLayout>{null}</AppLayout>;

  const teamFitAvg = avgTraits(persona?.teamFitTraits);
  const topTraits = persona
    ? (Object.entries(persona.teamFitTraits) as [keyof TeamFitTraits, number][])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
    : [];
  const upcomingInterview = interviews.find((iv) => iv.status === "in_progress");
  const recommended = [...matches].sort((a, b) => b.team_fit_score - a.team_fit_score).slice(0, 3);
  const activity = buildActivitySeries(resumes, interviews);
  const trendUp = activity[activity.length - 1].value >= activity[Math.max(0, activity.length - 2)].value;

  const filteredMatches = appFilter === "all" ? matches : matches.filter((m) => m.status === appFilter);
  const statusCounts = matches.reduce<Record<string, number>>((acc, m) => {
    acc[m.status] = (acc[m.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <AppLayout>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
        {/* ============ Main column ============ */}
        <div className="min-w-0 space-y-6">
          {/* Hero */}
          <Card className="overflow-hidden border border-slate-100 !p-0">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px]">
              <div className="bg-gradient-to-br from-navy via-navy to-navy-light p-5 text-white sm:p-7">
                <p className="text-xs font-medium text-white/60">
                  Good morning, {user?.name?.split(" ")[0] ?? "there"} 👋
                </p>
                <h1 className="mt-1.5 text-xl font-bold leading-snug sm:text-2xl">
                  Your unique persona<br className="hidden sm:block" /> opens more opportunities.
                </h1>
                <p className="mt-2 max-w-md text-xs leading-relaxed text-white/60">
                  Discover global opportunities perfectly matched to your experience, skills, and work style.
                </p>
                <div className="mt-5 flex flex-wrap gap-4 sm:gap-8">
                  <Stat label="Active Applications" value={matches.length} />
                  <Stat label="AI Resumes Created" value={resumes.length} />
                  <Stat label="Team-fit Score" value={`${teamFitAvg}%`} />
                </div>
              </div>
              <div className="flex flex-col justify-center gap-3 border-t border-slate-100 bg-white p-5 lg:border-l lg:border-t-0">
                <div className="flex items-center gap-3">
                  <ScoreRing value={teamFitAvg} size={56} strokeWidth={6} />
                  <div>
                    <p className="text-[11px] text-slate-400">Your Career Goal</p>
                    <p className="text-sm font-semibold text-navy">{persona?.careerGoal || "Not set yet"}</p>
                  </div>
                </div>
                <Link
                  to="/app/persona"
                  className="inline-flex items-center justify-center rounded-full bg-slate-50 px-3 py-2 text-xs font-semibold text-navy transition hover:bg-slate-100"
                >
                  Edit Goal
                </Link>
              </div>
            </div>
          </Card>

          {error && (
            <Card className="border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>
          )}

          {/* Persona Card */}
          <Card className="border border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-navy sm:text-base">My Persona Card</p>
              <Link to="/app/persona" className="text-xs font-medium text-teal-dark">
                View Detail →
              </Link>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[200px_1fr_200px]">
              {/* Identity */}
              <div>
                <div className="flex items-start gap-3">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-teal to-coral text-lg font-bold text-white">
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      user?.name?.[0] ?? "?"
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-navy">{user?.name}</p>
                    <p className="text-xs text-slate-500">{persona?.headline || "Set your headline"}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {persona?.location || "Location not set"}
                      {persona?.remotePreference ? ` · ${persona.remotePreference}` : ""}
                    </p>
                  </div>
                </div>
                {persona?.bio && (
                  <p className="mt-3 line-clamp-3 text-[11px] leading-relaxed text-slate-500">{persona.bio}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(persona?.skills ?? []).slice(0, 4).map((s) => (
                    <span key={s} className="rounded-full bg-teal/10 px-2 py-1 text-[10px] font-medium text-teal-dark">
                      {s}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex gap-3">
                  <Link to="/app/persona" className="text-xs font-medium text-teal-dark">✏️ Edit Profile</Link>
                  <button
                    className={`text-xs font-semibold transition ${
                      shareCopied ? "text-emerald-600" : "text-slate-400 hover:text-navy"
                    }`}
                    onClick={handleSharePersona}
                  >
                    {shareCopied ? "✓ Link Copied!" : "🔗 Share Persona"}
                  </button>
                </div>
              </div>

              {/* Radar */}
              <div className="flex flex-col items-center border-y border-slate-100 py-4 lg:border-x lg:border-y-0 lg:px-4 lg:py-0">
                <p className="mb-1 self-start text-xs font-semibold text-navy">Team-fit Radar</p>
                <RadarChart traits={persona?.teamFitTraits ?? {
                  communication: 0, collaboration: 0, leadership: 0, problemSolving: 0, adaptability: 0, integrity: 0,
                }} size={220} />
              </div>

              {/* Key Strengths */}
              <div>
                <p className="mb-2 text-xs font-semibold text-navy">Key Strengths</p>
                <div className="space-y-3">
                  {topTraits.length === 0 && (
                    <p className="text-[11px] text-slate-400">Complete your Persona to see your strengths.</p>
                  )}
                  {topTraits.map(([key]) => (
                    <div key={key} className="flex items-start gap-2">
                      <span className="text-base leading-none">{STRENGTH_COPY[key].icon}</span>
                      <div>
                        <p className="text-xs font-semibold text-navy">{STRENGTH_COPY[key].title}</p>
                        <p className="text-[10px] leading-relaxed text-slate-400">{STRENGTH_COPY[key].desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* AI Career Tools */}
          <div>
            <h2 className="mb-3 text-sm font-semibold text-navy sm:text-base">AI Powered Career Tools</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { to: "/app/resume", icon: "📝", title: "AI Adaptive Resume", desc: "Automatically adapts your resume and cover letter for target companies and roles.", cta: "Create Resume" },
                { to: "/app/matches", icon: "🤝", title: "Team-fit Matchmaker", desc: "Finds teams that perfectly match your work culture and communication style.", cta: "Find Matching Teams" },
                { to: "/app/interview", icon: "💬", title: "AI Chat Interview", desc: "Prepare thoroughly with interactive AI chat interviews that simulate real hiring.", cta: "Start Interview" },
                { to: "/app/persona", icon: "📊", title: "My Reports", desc: "Track your growth with personalized AI career analysis and insights.", cta: "View Reports" },
              ].map((tool) => (
                <Card key={tool.to} className="flex h-full flex-col border border-slate-100 transition hover:shadow-card-hover">
                  <div className="mb-2 text-xl">{tool.icon}</div>
                  <p className="text-xs font-semibold text-navy sm:text-sm">{tool.title}</p>
                  <p className="mt-1 flex-1 text-[11px] leading-relaxed text-slate-400">{tool.desc}</p>
                  <Link to={tool.to} className="mt-3 text-[11px] font-semibold text-teal-dark">
                    {tool.cta} →
                  </Link>
                </Card>
              ))}
            </div>
          </div>

          {/* Applications + Career growth */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]">
            <Card className="border border-slate-100">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-navy">My Applications</p>
                <Link to="/app/matches" className="text-xs font-medium text-teal-dark">View All →</Link>
              </div>
              <div className="mt-3 flex gap-2 overflow-x-auto">
                <FilterChip active={appFilter === "all"} onClick={() => setAppFilter("all")}>
                  All ({matches.length})
                </FilterChip>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <FilterChip key={status} active={appFilter === status} onClick={() => setAppFilter(status)}>
                    {status} ({count})
                  </FilterChip>
                ))}
              </div>

              {filteredMatches.length === 0 ? (
                <p className="mt-4 text-center text-xs text-slate-400">
                  No team proposals yet. Complete your Persona to receive offers from companies.
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full min-w-[420px] text-left text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-400">
                        <th className="pb-2 font-medium">Company</th>
                        <th className="pb-2 font-medium">Match Score</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatches.slice(0, 6).map((m) => (
                        <tr key={m.id} className="border-t border-slate-50">
                          <td className="py-2.5 font-medium text-navy">{m.company_name}</td>
                          <td className="py-2.5">
                            <span className="font-semibold text-teal-dark">{m.team_fit_score}%</span>
                          </td>
                          <td className="py-2.5">
                            <StatusPill status={m.status} />
                          </td>
                          <td className="py-2.5 text-slate-400">
                            {new Date(m.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card className="border border-slate-100">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-navy">My Career Growth</p>
                <span className="text-[10px] text-slate-400">6 Month</span>
              </div>
              <div className="mt-4">
                <BarChart data={activity} />
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-teal/5 px-3 py-2.5">
                <span className="text-base">{trendUp ? "🚀" : "🌱"}</span>
                <p className="text-[11px] leading-relaxed text-slate-500">
                  {trendUp
                    ? "You're growing fast! Keep creating resumes and practicing interviews."
                    : "Keep going — try creating a resume or starting a practice interview this month."}
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* ============ Right sidebar ============ */}
        <div className="space-y-6">
          <Card className="border border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-navy">Recommended For You</p>
              <Link to="/app/matches" className="text-[11px] font-medium text-teal-dark">See All →</Link>
            </div>
            <div className="mt-3 space-y-3">
              {recommended.length === 0 && (
                <p className="text-[11px] text-slate-400">Complete your Persona to get matched with companies.</p>
              )}
              {recommended.map((m) => (
                <Link key={m.id} to="/app/matches" className="flex items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy to-teal text-xs font-bold text-white">
                    {m.company_name.slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-navy">{m.company_name}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(m.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-[11px] font-bold text-teal-dark">{m.team_fit_score}%</span>
                </Link>
              ))}
            </div>
          </Card>

          <Card className="border border-slate-100">
            <p className="text-sm font-semibold text-navy">Upcoming Interview</p>
            <div className="mt-3">
              {upcomingInterview ? (
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs font-semibold text-navy">{upcomingInterview.title}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    {upcomingInterview.question_count} questions answered so far
                  </p>
                  <Link
                    to={`/app/interview/${upcomingInterview.id}`}
                    className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-navy px-3 py-2 text-[11px] font-semibold text-white"
                  >
                    Continue Interview
                  </Link>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 p-3 text-center">
                  <p className="text-[11px] text-slate-400">No interview in progress.</p>
                  <Link
                    to="/app/interview"
                    className="mt-2 inline-flex items-center justify-center rounded-full bg-teal px-3 py-1.5 text-[11px] font-semibold text-white"
                  >
                    Start Interview
                  </Link>
                </div>
              )}
            </div>
          </Card>

          <Card className="border border-slate-100">
            <p className="text-sm font-semibold text-navy">Recent Activity</p>
            <div className="mt-3 space-y-3">
              {resumes.slice(0, 2).map((r) => (
                <div key={r.id} className="flex items-start gap-2">
                  <div className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-teal" />
                  <div>
                    <p className="text-xs font-medium text-navy">AI Resume Created</p>
                    <p className="text-[10px] text-slate-400">{r.target_company_name || "General"}</p>
                  </div>
                </div>
              ))}
              {interviews.slice(0, 2).map((iv) => (
                <div key={iv.id} className="flex items-start gap-2">
                  <div className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-coral" />
                  <div>
                    <p className="text-xs font-medium text-navy">Interview: {iv.title}</p>
                    <p className="text-[10px] text-slate-400">{iv.status} · {iv.question_count} questions</p>
                  </div>
                </div>
              ))}
              {resumes.length === 0 && interviews.length === 0 && (
                <p className="text-xs text-slate-400">No recent activity yet.</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-lg font-bold sm:text-xl">{value}</p>
      <p className="text-[10px] text-white/50 sm:text-[11px]">{label}</p>
    </div>
  );
}

function FilterChip({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
        active ? "bg-navy text-white" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    invited: "bg-teal/10 text-teal-dark",
    accepted: "bg-emerald-50 text-emerald-600",
    declined: "bg-coral/10 text-coral",
  };
  return (
    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold capitalize ${colorMap[status] ?? "bg-slate-50 text-slate-500"}`}>
      {status}
    </span>
  );
}
